import { useQuery } from "@tanstack/react-query";

import {
  getCurrentUserQueryOptions,
  sharedWithMeQueryOptions,
  type FeedCapability,
} from "@/lib/api";
import type { Feed } from "@/lib/types";

/**
 * What the person looking at this feed is allowed to do to it.
 *
 * Reads the "shared with you" list rather than asking per-feed: the feeds index already
 * warms that query, it is one request however many feeds you open, and it carries the
 * capabilities for every feed at once.
 *
 * The owner is not in that list and does not need to be — owning the feed implies all of it.
 * This is a convenience for rendering, NOT the authority: every action is checked again
 * server-side, so a stale or wrong answer here costs a 403, never an unauthorised change.
 */
export function useFeedAccess(feed: Pick<Feed, "id" | "user_id">) {
  const { data: user } = useQuery(getCurrentUserQueryOptions());
  const byId = useFeedAccessById(feed.id);

  const isOwner = !!user && user.id === feed.user_id;

  return {
    ...byId,
    isOwner,
    /** True when this feed reached you through a grant rather than by owning it. */
    isCollaborator: !isOwner && byId.isCollaborator,
    can: (capability: FeedCapability) => isOwner || byId.can(capability),
  };
}

/**
 * The same answer where only the feed id is to hand.
 *
 * Deep inside the editors there is no `Feed` object, and threading the owner's id through
 * four components to compute a boolean they already imply is worse than this: being in the
 * shared-with-me list IS being a collaborator, because an owner is never in it.
 *
 * 🔑 `can` IS TRUE FOR A NON-COLLABORATOR. Not being in that list means you are the owner —
 * or a stranger, who the server refuses whatever this returns. It renders controls; it
 * does not grant anything.
 */
export function useFeedAccessById(feedId: number) {
  const { data: shared } = useQuery(sharedWithMeQueryOptions());

  // Same hazard as the dashboard's `hasShared`: the response can arrive without its array.
  // This hook now runs inside the editors, so a throw here blanks the feed page.
  const entry = shared?.feeds?.find((f) => f.id === feedId);
  const capabilities: FeedCapability[] = entry?.capabilities ?? [];

  return {
    // 🔑 A GRANT OF NOTHING IS NOT A GRANT. The server's read check is
    // `bool(capabilities)`, so a row stripped to zero capabilities already means 403 on the
    // feed page -- while `/shared-with-me` still lists the row, because it filters on
    // deletion rather than on power. Treating that as "not a collaborator" keeps this hook
    // agreeing with the server instead of promising access the server refuses.
    isCollaborator: capabilities.length > 0,
    capabilities,
    can: (capability: FeedCapability) =>
      !entry || capabilities.includes(capability),
  };
}
